import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type BannerLangViewModel,
  getApiV1BannerBanneridLangsQueryOptions,
  useDeleteApiV1BannerBanneridLangsId,
  useGetApiV1BannerBanneridLangs,
  usePostApiV1BannerBanneridLangs,
  usePutApiV1BannerBanneridLangsId,
} from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import {
  BANNER_TRANSLATION_LANGUAGES,
  type BannerFormValues,
  isTranslationFilled,
} from '../types/bannerForm';

/**
 * Mídia da tradução: imagem ou vídeo, nunca os dois — o tipo segue o que o
 * operador escolheu no cadastro em português. O mesmo arquivo vai nos dois pares
 * (normal e mobile); a tela exibe só o mobile.
 */
function mediaPayload(translation: BannerFormValues['translations']['en-US']) {
  if (translation.videoFile) {
    return { Video: translation.videoFile, VideoMobile: translation.videoFile };
  }
  if (translation.imageFile) {
    return { Image: translation.imageFile, ImageMobile: translation.imageFile };
  }
  return {};
}

/**
 * Hook de leitura e escrita das traduções de um Banner
 * (`bannerIdiomasController`).
 *
 * Leitura: `GET /api/v1/banner/{bannerId}/langs`, normalizada com
 * `unwrapPagedResponse` (o backend envelopa listagens de forma inconsistente).
 *
 * Escrita: `saveTranslations` decide por aba de idioma — `POST` quando a
 * tradução foi preenchida e ainda não existe, `PUT` quando já existe, `DELETE`
 * quando foi esvaziada. Roda em sequência **depois** do banner ser salvo,
 * porque os endpoints exigem `bannerId`.
 *
 * O payload não leva título: `POST/PUT .../langs` aceita apenas `CultureInfo`,
 * `Url`, `Active` e a mídia — o título da tradução é uma lacuna conhecida do
 * backend. A arte vai em `ImageMobile`, o mesmo par usado no banner.
 *
 * @param bannerId - Banner em edição; `undefined` na criação (sem leitura).
 *
 * @example
 * ```tsx
 * const { langs, saveTranslations } = useBannerTranslations(bannerId);
 * await saveTranslations(savedBannerId, values);
 * ```
 */
export function useBannerTranslations(bannerId: number | undefined) {
  const queryClient = useQueryClient();
  const query = useGetApiV1BannerBanneridLangs(bannerId ?? 0, undefined, {
    query: { enabled: bannerId !== undefined },
  });

  const createLang = usePostApiV1BannerBanneridLangs();
  const updateLang = usePutApiV1BannerBanneridLangsId();
  const deleteLang = useDeleteApiV1BannerBanneridLangsId();

  // Memoizado de propósito: `unwrapPagedResponse` devolve um array novo a cada
  // chamada, e essa lista alimenta o `reset` do formulário. Sem memo, a
  // identidade muda em todo render e o `useEffect` do template reseta o
  // formulário sem parar — apagando o que o operador acabou de digitar.
  const langs = useMemo(() => unwrapPagedResponse<BannerLangViewModel>(query.data), [query.data]);

  /**
   * Persiste as traduções preenchidas e apaga as que foram esvaziadas.
   *
   * @param targetBannerId - ID do banner já salvo (criado ou atualizado).
   * @param values - Valores atuais do formulário.
   */
  const saveTranslations = async (targetBannerId: number, values: BannerFormValues) => {
    for (const culture of BANNER_TRANSLATION_LANGUAGES) {
      const translation = values.translations[culture];
      const filled = isTranslationFilled(translation);

      if (filled && translation.langId === undefined) {
        await createLang.mutateAsync({
          bannerId: targetBannerId,
          data: {
            CultureInfo: culture,
            Url: translation.url,
            Active: values.active,
            ...mediaPayload(translation),
          },
        });
        continue;
      }

      if (filled && translation.langId !== undefined) {
        await updateLang.mutateAsync({
          bannerId: targetBannerId,
          id: translation.langId,
          data: {
            CultureInfo: culture,
            Url: translation.url,
            Active: values.active,
            ...mediaPayload(translation),
          },
        });
        continue;
      }

      if (!filled && translation.langId !== undefined) {
        await deleteLang.mutateAsync({ bannerId: targetBannerId, id: translation.langId });
      }
    }

    void queryClient.invalidateQueries({
      queryKey: getApiV1BannerBanneridLangsQueryOptions(targetBannerId).queryKey,
    });
  };

  return {
    langs,
    isLoading: bannerId !== undefined && query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    saveTranslations,
    isPending: createLang.isPending || updateLang.isPending || deleteLang.isPending,
  } as const;
}
